import { z } from 'zod';
import { FieldKeySchema, FieldTypeSchema } from './fields/registry';
import { ConditionSchema } from './query/ast';

export const FORM_KINDS = ['create', 'edit', 'view', 'portal', 'transition'] as const;
export const FormKindSchema = z.enum(FORM_KINDS);
export type FormKind = z.infer<typeof FormKindSchema>;

/** System (non-custom) fields a form layout may place. */
export const FORM_SYSTEM_FIELDS = [
  'summary',
  'description',
  'priority_id',
  'assignee_id',
  'labels',
  'due_at',
] as const;
export const FormSystemFieldSchema = z.enum(FORM_SYSTEM_FIELDS);
export type FormSystemField = z.infer<typeof FormSystemFieldSchema>;

export const FormFieldKeySchema = z.union([FormSystemFieldSchema, FieldKeySchema]);
export type FormFieldKey = z.infer<typeof FormFieldKeySchema>;

/**
 * One field placed on a form. `visibleWhen` reuses the query AST condition; when it
 * evaluates false the field is hidden, submitted values are stripped and `required` does
 * not apply (ADR-0013 §4). `required` here can only tighten the binding, never loosen it.
 */
export const FormFieldLayoutSchema = z.strictObject({
  key: FormFieldKeySchema,
  /** Overrides the field label on this form only. */
  label: z.string().min(1).max(80).optional(),
  required: z.boolean().optional(),
  readOnly: z.boolean().optional(),
  visibleWhen: ConditionSchema.optional(),
});
export type FormFieldLayout = z.infer<typeof FormFieldLayoutSchema>;

export const FormSectionSchema = z.strictObject({
  title: z.string().min(1).max(80).optional(),
  description: z.string().max(500).optional(),
  fields: z.array(FormFieldLayoutSchema).max(100),
});
export type FormSection = z.infer<typeof FormSectionSchema>;

export const FormLayoutSchema = z
  .strictObject({
    sections: z.array(FormSectionSchema).min(1).max(20),
  })
  .superRefine((layout, ctx) => {
    const seen = new Set<string>();
    layout.sections.forEach((section, s) => {
      section.fields.forEach((field, f) => {
        if (seen.has(field.key)) {
          ctx.addIssue({
            code: 'custom',
            message: `field ${field.key} appears more than once`,
            path: ['sections', s, 'fields', f, 'key'],
          });
        }
        seen.add(field.key);
      });
    });
  });
export type FormLayout = z.infer<typeof FormLayoutSchema>;

export const FormSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  /** Null = applies to every issue type of the project. */
  issueTypeId: z.uuid().nullable(),
  kind: FormKindSchema,
  name: z.string().min(1).max(80),
  layout: FormLayoutSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Form = z.infer<typeof FormSchema>;

export const CreateFormSchema = z.strictObject({
  issueTypeId: z.uuid().nullable().default(null),
  kind: FormKindSchema,
  name: z.string().min(1).max(80),
  layout: FormLayoutSchema,
});
export type CreateForm = z.infer<typeof CreateFormSchema>;

export const UpdateFormSchema = z
  .strictObject({
    issueTypeId: z.uuid().nullable(),
    kind: FormKindSchema,
    name: z.string().min(1).max(80),
    layout: FormLayoutSchema,
  })
  .partial();
export type UpdateForm = z.infer<typeof UpdateFormSchema>;

/* ----------------------------- Bolt 4: resolved form ----------------------------- */

/**
 * One field of a resolved form (`GET .../forms/{id}/resolved`): the effective label,
 * required / read-only flags for the caller and, for select fields, the active options.
 * Fields the caller may not read are absent.
 */
export const ResolvedFormFieldSchema = z.object({
  key: FormFieldKeySchema,
  type: FieldTypeSchema,
  label: z.string(),
  required: z.boolean(),
  readOnly: z.boolean(),
  /** True for system fields (summary, description, ...). */
  system: z.boolean(),
  visibleWhen: ConditionSchema.optional(),
  options: z
    .array(z.object({ id: z.uuid(), label: z.string(), color: z.string().nullable() }))
    .optional(),
});
export type ResolvedFormField = z.infer<typeof ResolvedFormFieldSchema>;

export const ResolvedFormSchema = z.object({
  formId: z.uuid(),
  kind: FormKindSchema,
  fields: z.array(ResolvedFormFieldSchema),
});
export type ResolvedForm = z.infer<typeof ResolvedFormSchema>;

/** `GET /projects/{id}/forms?kind=&issueTypeId=`. */
export const FormListQuerySchema = z.object({
  kind: FormKindSchema.optional(),
  issueTypeId: z.uuid().optional(),
});
export type FormListQuery = z.infer<typeof FormListQuerySchema>;
